import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, signal, viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  GlobalSearchService, GrupoDeResultados, ResultadoDeBusqueda,
} from '@shared/services/busqueda/global-search.service';

/**
 * La búsqueda global del producto, con su atajo (Ctrl+K).
 *
 * <p><b>Lo que faltaba.</b> Seis requerimientos de cinco módulos la dan por existente —M15.05-RF-024
 * la llama por su atajo— y no estaba construida. Lo que había eran buscadores por módulo, cada uno
 * dentro de su pantalla: para encontrar algo había que saber antes dónde vivía.
 *
 * <p><b>Se navega con el teclado, porque se abre con el teclado.</b> Quien pulsa Ctrl+K no va a
 * soltar el teclado para coger el ratón. Flechas para moverse, Enter para abrir, Escape para
 * cerrar; el resultado activo se marca con {@code aria-selected} y el campo declara qué lista
 * controla, que es lo que hace que un lector de pantalla pueda seguir el recorrido.
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (abierta()) {
      <div class="fixed inset-0 z-[100000] flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm"
           role="dialog" aria-modal="true" aria-labelledby="busqueda-global-titulo"
           (click)="cerrarSiEsElFondo($event)">
        <div class="w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900">

          <h2 id="busqueda-global-titulo" class="sr-only">Búsqueda global</h2>

          <div class="flex items-center gap-2 border-b border-gray-100 px-4 dark:border-gray-800">
            <i class="bi bi-search text-gray-400" aria-hidden="true"></i>
            <input #campo
                   type="search"
                   class="w-full bg-transparent py-3.5 text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-gray-100"
                   placeholder="Buscar tareas, proyectos, contactos, commits…"
                   aria-label="Buscar en todo el producto"
                   role="combobox"
                   aria-expanded="true"
                   aria-controls="busqueda-global-resultados"
                   [attr.aria-activedescendant]="activo() >= 0 ? 'resultado-' + activo() : null"
                   [ngModel]="consulta()"
                   (ngModelChange)="alEscribir($event)" />
            <kbd class="hidden rounded border border-gray-200 px-1.5 py-0.5 text-[10px] text-gray-400 sm:inline dark:border-gray-700">Esc</kbd>
          </div>

          <div id="busqueda-global-resultados" role="listbox" class="max-h-[60vh] overflow-y-auto p-2"
               [attr.aria-label]="'Resultados de la búsqueda'">
            @if (buscando()) {
              <p class="px-3 py-6 text-center text-xs text-gray-400">Buscando…</p>
            } @else if (consulta().trim().length < MINIMO) {
              <p class="px-3 py-6 text-center text-xs text-gray-400">
                Escribe al menos {{ MINIMO }} caracteres.
              </p>
            } @else if (!grupos().length) {
              <p class="px-3 py-6 text-center text-xs text-gray-400">
                Nada coincide con «{{ consulta() }}».
              </p>
            } @else {
              @for (grupo of grupos(); track grupo.categoria) {
                <p class="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {{ grupo.categoria }}
                </p>
                @for (r of grupo.resultados; track r.entidad + '-' + r.entidadId) {
                  <button type="button"
                          [id]="'resultado-' + indiceDe(r)"
                          role="option"
                          [attr.aria-selected]="activo() === indiceDe(r)"
                          class="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left"
                          [ngClass]="activo() === indiceDe(r) ? 'bg-brand-50 dark:bg-brand-500/10' : ''"
                          (mouseenter)="activo.set(indiceDe(r))"
                          (click)="abrir(r)">
                    @if (r.insignia) {
                      <span class="mt-0.5 shrink-0 rounded border border-gray-200 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:border-gray-700">
                        {{ r.insignia }}
                      </span>
                    }
                    <span class="min-w-0 flex-1">
                      <span class="block truncate text-sm text-gray-800 dark:text-gray-100">{{ r.titulo }}</span>
                      @if (r.subtitulo) {
                        <span class="block truncate text-[11px] text-gray-400">{{ r.subtitulo }}</span>
                      }
                      @if (r.extracto) {
                        <span class="mt-0.5 block truncate text-[11px] text-gray-500 dark:text-gray-400">{{ r.extracto }}</span>
                      }
                    </span>
                  </button>
                }
              }
            }
          </div>
        </div>
      </div>
    }
  `,
})
export class GlobalSearchComponent {
  private readonly servicio = inject(GlobalSearchService);
  private readonly router = inject(Router);

  protected readonly MINIMO = GlobalSearchService.MINIMO_CARACTERES;

  readonly abierta = signal(false);
  readonly consulta = signal('');
  readonly buscando = signal(false);
  readonly grupos = signal<GrupoDeResultados[]>([]);
  /** Índice del resultado bajo el cursor del teclado, sobre la lista aplanada. */
  readonly activo = signal(-1);

  private readonly campo = viewChild<ElementRef<HTMLInputElement>>('campo');

  /**
   * Los resultados en una sola lista.
   *
   * <p>Se pintan agrupados pero se recorren seguidos: con las flechas, bajar del último «Proyecto»
   * al primer «Contacto» tiene que ser una pulsación más, no un salto que el usuario deba descubrir.
   */
  private readonly planos = computed<ResultadoDeBusqueda[]>(
    () => this.grupos().flatMap(g => g.resultados));

  private readonly teclea = new Subject<string>();

  constructor() {
    // Se consulta mientras se escribe, pero no en cada tecla: `debounce` evita una petición por
    // pulsación, y `distinctUntilChanged` evita repetir la misma al borrar y reescribir una letra.
    // `switchMap` cancela la anterior, que es lo que impide que una respuesta lenta de hace tres
    // letras pise a la de la consulta actual.
    this.teclea.pipe(
      debounceTime(220),
      distinctUntilChanged(),
      switchMap(q => {
        if (q.trim().length < this.MINIMO) {
          this.buscando.set(false);
          this.grupos.set([]);
          return [];
        }
        this.buscando.set(true);
        return this.servicio.buscar(q.trim());
      }),
      takeUntilDestroyed(),
    ).subscribe({
      next: grupos => {
        this.grupos.set(Array.isArray(grupos) ? grupos : []);
        this.buscando.set(false);
        this.activo.set(this.planos().length ? 0 : -1);
      },
      // Un fallo deja la paleta abierta y vacía: cerrarla de golpe haría perder lo escrito sin
      // decir por qué.
      error: () => { this.grupos.set([]); this.buscando.set(false); },
    });
  }

  // ══════════════════ Atajos ══════════════════

  /**
   * Ctrl+K (y Cmd+K en Mac) abre; Escape cierra.
   *
   * <p>Va en `document` y no en el componente porque el atajo tiene que funcionar con el foco en
   * cualquier sitio — que es lo que lo hace un atajo global y no un control más de la cabecera.
   */
  @HostListener('document:keydown', ['$event'])
  alPulsarTecla(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      this.alternar();
      return;
    }
    if (!this.abierta()) return;

    if (e.key === 'Escape') { e.preventDefault(); this.cerrar(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.mover(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); this.mover(-1); return; }
    if (e.key === 'Enter') {
      const elegido = this.planos()[this.activo()];
      if (elegido) { e.preventDefault(); this.abrir(elegido); }
    }
  }

  alternar(): void {
    if (this.abierta()) { this.cerrar(); return; }
    this.abierta.set(true);
    // El foco va al campo tras pintar: enfocar un elemento que todavía no está en el DOM no hace
    // nada, y la paleta se abriría sin poder escribir en ella.
    queueMicrotask(() => this.campo()?.nativeElement.focus());
  }

  cerrar(): void {
    this.abierta.set(false);
    this.consulta.set('');
    this.grupos.set([]);
    this.activo.set(-1);
  }

  cerrarSiEsElFondo(e: MouseEvent): void {
    if (e.target === e.currentTarget) this.cerrar();
  }

  // ══════════════════ Interacción ══════════════════

  alEscribir(valor: string): void {
    this.consulta.set(valor);
    this.teclea.next(valor);
  }

  indiceDe(r: ResultadoDeBusqueda): number {
    return this.planos().findIndex(x => x.entidad === r.entidad && x.entidadId === r.entidadId);
  }

  /** Mueve el cursor, dando la vuelta por los extremos. */
  private mover(paso: number): void {
    const total = this.planos().length;
    if (!total) return;
    this.activo.set((this.activo() + paso + total) % total);
  }

  /**
   * Abre el resultado.
   *
   * <p>El enlace llega del servidor con su consulta —`/nexus/tareas?task=42`—, así que se parte en
   * ruta y parámetros: pasárselo entero a `navigateByUrl` funciona, pero deja el router sin los
   * parámetros tipados y rompe el `queryParams` de la pantalla de destino.
   */
  abrir(r: ResultadoDeBusqueda): void {
    const [ruta, consulta] = r.enlace.split('?');
    const params: Record<string, string> = {};
    for (const par of (consulta ?? '').split('&')) {
      if (!par) continue;
      const [k, v] = par.split('=');
      params[k] = decodeURIComponent(v ?? '');
    }
    this.cerrar();
    void this.router.navigate([ruta], { queryParams: params });
  }
}
